import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import {
  type AuthFailureReason,
  getAuthFailureReason,
  trialOfferParamKey,
} from '@/modules/auth/constants';
import { useExternalLoginMutation } from '@/modules/auth/mutations/useExternalLoginMutation';
import {
  classifyOAuthFailure,
  type OAuthFailureKind,
  type OAuthSignInRequest,
} from '@/modules/auth/oauthCallbackFailure';
import type { ExternalLoginRequest } from '@/modules/auth/types/externalLogin';
import { getRoleBasedReturnTo } from '@/modules/auth/utils/getRoleBasedReturnTo';

import type { OAuthCallbackProvider } from './OAuthCallbackStatus';

/** What one provider's callback needs; see `GoogleSignInCallback.tsx` and friends. */
export interface OAuthCallbackAdapter {
  provider: OAuthCallbackProvider;
  /** Its `*SSO` analytics event, with the same stages for every provider. */
  ssoEvent: 'googleSSO' | 'slackSSO' | 'linkedInSSO';
  /** The `signUpFailed`/`signInFailed` reason when the provider module threw. */
  ssoFailureReason: Extract<AuthFailureReason, `${string}_sso_error`>;
  /** Validates the callback URL and returns what to send to the external login. */
  complete: (callbackUrl: string) => Promise<{
    request: OAuthSignInRequest;
    credentials: Omit<ExternalLoginRequest, 'trialOfferCode' | 'acceptTerms'>;
  }>;
  /** The stored sign-in, read without validating, for a failed callback. */
  readRequest: (callbackUrl: string) => Promise<OAuthSignInRequest | undefined>;
  clearState: () => void | Promise<void>;
  /** Restarts the sign-in at the provider. */
  start: (request: OAuthSignInRequest) => void | Promise<void>;
  /** The provider module's error code, when it threw its own error. */
  getSsoErrorCode: (error: unknown) => string | undefined;
}

const readTrialOfferCode = (returnTo: string) => {
  try {
    return (
      new URL(returnTo, window.location.origin).searchParams.get(
        trialOfferParamKey,
      ) ?? undefined
    );
  } catch {
    return undefined;
  }
};

/**
 * Finishes an OAuth sign-in on its callback route: sends the provider's
 * credential to the API's external login, then opens the app. On failure it
 * returns why, plus what `OAuthCallbackStatus` needs to offer a way back or a
 * restart.
 */
export const useOAuthCallback = (adapter: OAuthCallbackAdapter) => {
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const externalLoginMutation = useExternalLoginMutation();
  const hasStarted = useRef(false);
  const adapterRef = useRef(adapter);

  const [failure, setFailure] = useState<OAuthFailureKind>();
  const [request, setRequest] = useState<OAuthSignInRequest>();

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const {
      provider,
      ssoEvent,
      ssoFailureReason,
      complete,
      readRequest,
      clearState,
      getSsoErrorCode,
    } = adapterRef.current;

    const completeSignIn = async () => {
      trackEvent(ssoEvent, { stage: 'callback_received' });

      const callbackUrl = window.location.href;
      // The query or fragment holds a one-time code or the ID token: keep it
      // out of the history entry.
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname,
      );

      // Set once the callback is validated; a failure before that reads it
      // from the stored state instead.
      let signInRequest: OAuthSignInRequest | undefined;
      try {
        const result = await complete(callbackUrl);
        signInRequest = result.request;
        setRequest(signInRequest);

        // The offer code rides in `returnTo`'s query string through the
        // provider's state; strip it so the one-time code stays out of app URLs.
        const returnTo = new URL(
          signInRequest.returnTo,
          window.location.origin,
        );
        const trialOfferCode = readTrialOfferCode(signInRequest.returnTo);
        returnTo.searchParams.delete(trialOfferParamKey);

        await externalLoginMutation.mutateAsync({
          ...result.credentials,
          trialOfferCode,
          acceptTerms: signInRequest.acceptTerms,
        });
        await clearState();

        trackEvent(ssoEvent, { stage: 'exchange_succeeded' });
        trackEvent(
          signInRequest.intent === 'signUp' ? 'userSignedUp' : 'userSignedIn',
          { authMethod: provider },
        );

        const cleanReturnTo = `${returnTo.pathname}${returnTo.search}${returnTo.hash}`;
        navigate(await getRoleBasedReturnTo(cleanReturnTo), { replace: true });
      } catch (error) {
        // Recovery must not throw: whatever happens here, the screen ends on
        // a failure outcome instead of staying on "Signing you in".
        try {
          signInRequest ??= await readRequest(callbackUrl);
        } catch {
          signInRequest = undefined;
        }
        try {
          await clearState();
        } catch {
          // Leftover state is cleared by the next sign-in.
        }

        const ssoErrorCode = getSsoErrorCode(error);
        trackEvent(ssoEvent, {
          stage: 'exchange_failed',
          errorCode: ssoErrorCode ?? 'exchange_failed',
        });
        // Without the stored state (e.g. the callback opened in another
        // browser) only the `*SSO` event above records the failure.
        if (signInRequest) {
          trackEvent(
            signInRequest.intent === 'signUp' ? 'signUpFailed' : 'signInFailed',
            {
              authMethod: provider,
              reason: ssoErrorCode
                ? ssoFailureReason
                : getAuthFailureReason(error, provider),
            },
          );
        }

        setRequest(signInRequest);
        setFailure(
          classifyOAuthFailure({
            error,
            ssoErrorCode,
            trialOfferCode:
              signInRequest && readTrialOfferCode(signInRequest.returnTo),
          }),
        );
      }
    };

    completeSignIn();
  }, [externalLoginMutation, navigate, trackEvent]);

  return {
    failure,
    intent: request?.intent,
    trialOfferCode: request && readTrialOfferCode(request.returnTo),
    onRetry: request ? () => adapterRef.current.start(request) : undefined,
  };
};
